import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { ApiError, RefreshButton, HelpIcon, LoadFailure, Skeleton, WORKER_ROLE_LABELS, isNotFoundError, useLoadingWindow, type WorkerRole } from '../../../core/index.js'
import { schedulingRulesOf, useFacilityConfig } from '../../../facilities/index.js'
import { DEMO_REFRESH_MS, useCensusRequirementsRange, useDecisions, useFacilityHeatmap, useMonthHealth, useScheduleBoard, useSchedulePeriod } from '../../hooks.js'
import { DayHeatmap } from '../heatmap/DayHeatmap.js'
import { filteredDay, HEAT_SWATCH, pctOf } from '../heatmap/heatmapData.js'
import { EngineModeModal, engineLabelOf } from './EngineModeModal.js'
import { addDays } from '../censusCardData.js'
import { todayDate } from '../scheduleCalendarData.js'
import { NeedsYou } from './NeedsYou.js'
import { OpenShiftsInFlight } from './OpenShiftsInFlight.js'
import { unitNamesOf } from '../boardText.js'
import { SetupList } from './SetupList.js'
import { TodayCensus } from './TodayCensus.js'
import { WeekAhead } from './WeekAhead.js'
import { coverageToday, openSeatsInFlight, setupSteps, useTodayFilter, weekDaySummary, type CoverageClock, type TodayFilter } from './todayData.js'

const ROLES: readonly WorkerRole[] = ['RN', 'LPN', 'CNA']
const CENSUS_ANCHOR = 'today-census'

/** Today's date where the facility is, so a facility three zones away counts the right day. */
function todayIn(timezone: string | undefined): string {
  if (!timezone) {
    return todayDate()
  }
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())
  } catch {
    return todayDate()
  }
}

/** The time where the facility is, `HH:mm`, so a cell knows its shift is over. */
function timeIn(timezone: string | undefined): string {
  const format = (zone?: string) => new Intl.DateTimeFormat('en-GB', { ...(zone ? { timeZone: zone } : {}), hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date())
  try {
    return format(timezone)
  } catch {
    return format()
  }
}

/** "Thursday, November 5". */
function longDay(date: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' })
}

/** "November". */
function monthName(period: string): string {
  return new Date(`${period}-01T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' })
}

function Tile({ label, value, note, tone = 'plain' }: { label: string; value: ReactNode; note: string; tone?: 'plain' | 'alert' }) {
  const frame = tone === 'alert' ? 'border-coral bg-white' : 'border-marine/15 bg-white'
  return (
    <div className={`flex min-w-0 flex-1 basis-48 flex-col gap-1 rounded border px-4 py-3 ${frame}`}>
      <span className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{label}</span>
      <span className={`text-2xl font-black leading-tight ${tone === 'plain' ? 'text-ink' : 'text-coral'}`}>{value}</span>
      <span className="text-[11px] leading-snug text-ink-mid">{note}</span>
    </div>
  )
}

function Section({ title, hint, help, children, className = '' }: { title: string; hint?: string; help?: string; children: ReactNode; className?: string }) {
  return (
    <section className={`flex min-w-0 flex-col gap-3 rounded border border-marine/15 bg-white px-4 py-3.5 ${className}`}>
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-marine">
          {title}
          {help && <HelpIcon topic={help} />}
        </h2>
        {hint && <span className="text-[11px] text-ink-mid">{hint}</span>}
      </div>
      {children}
    </section>
  )
}

/**
 * The section the Today tab opens on: Needs you, or the set-up list for a facility with nothing to run. It always
 * comes first, above the tiles, and carries its own count, so the page never changes shape (a facility administrator
 * sees what needs them before anything else).
 */
function LeadSection({ title, count, alert, hint, help, children }: { title: string; count: string | null; alert: boolean; hint: string; help: string; children: ReactNode }) {
  return (
    <section className="flex min-w-0 flex-col gap-3 rounded border border-t-[3px] border-marine/15 border-t-coral bg-white px-4 py-3.5" aria-label={title}>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="inline-flex items-center gap-1 text-[15px] font-black text-ink">
          {title}
          <HelpIcon topic={help} />
        </h2>
        {count !== null && <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${alert ? 'bg-coral text-white' : 'bg-marine-lt text-marine-dk'}`}>{count}</span>}
        <span className="text-[11px] text-ink-mid">{hint}</span>
      </div>
      {children}
    </section>
  )
}

function FilterBar({ filter, onChange, units }: { filter: TodayFilter; onChange: (next: TodayFilter) => void; units: readonly { id: string; name: string }[] }) {
  const pill = (active: boolean) => `rounded-full border px-2.5 py-1 text-[11px] font-bold ${active ? 'border-marine bg-marine text-white' : 'border-marine/25 bg-white text-ink-mid hover:border-marine'}`
  if (units.length <= 1) {
    return null
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Show one unit or role">
      <span className="mr-1 text-[10px] font-bold uppercase tracking-wide text-ink-mid">Units</span>
      <button type="button" className={pill(filter.unitId === null)} onClick={() => onChange({ ...filter, unitId: null })}>
        All {units.length}
      </button>
      {units.map((unit) => (
        <button key={unit.id} type="button" className={pill(filter.unitId === unit.id)} onClick={() => onChange({ ...filter, unitId: unit.id })}>
          {unit.name}
        </button>
      ))}
      <span className="ml-3 mr-1 text-[10px] font-bold uppercase tracking-wide text-ink-mid">Roles</span>
      <button type="button" className={pill(filter.role === null)} onClick={() => onChange({ ...filter, role: null })}>
        All
      </button>
      {ROLES.map((role) => (
        <button key={role} type="button" className={pill(filter.role === role)} title={WORKER_ROLE_LABELS[role]} onClick={() => onChange({ ...filter, role })}>
          {role}
        </button>
      ))}
    </div>
  )
}

export interface TodayPanelProps {
  /** Whether the Today tab is on screen: it opens the loading window only then. */
  shown: boolean
  facilityId: string
  /** Only a facility administrator opens a Clinical Bridge callout. */
  role: 'operator' | 'facility_administrator'
  rosterCount: number
  names: ReadonlyMap<string, string>
  configHref: string
  /** This facility's Dashboard on its Month tab. */
  monthHref: string
  onAddWorker: () => void
  /** What the engine did today: the activity feed's engine filter, composed by the page. */
  engineLog: ReactNode
  /** Whether the client is a demo: the two-minute demo's Play control shows then (open shift lifecycle plan D25). */
  isDemo?: boolean
  /** The Clinical Bridge callouts, composed by the page. */
  bridge: ReactNode
}

/**
 * The Dashboard's Today tab (facility operations dashboard plan §3): the facility's day in one place - first what needs
 * a person (or, for a facility with nothing to run, its set-up list), then three tiles, the day at a glance (the heatmap,
 * Today's one coverage view since 27 October 2026, design screen 13: the week strip under Census chooses the day it
 * draws, with the open seats in the order they lock beside its grid), today's census with Record beside what the engine
 * did, and the Clinical Bridge callouts. A unit and role filter narrows every section and is remembered.
 */
export function TodayPanel({ shown, facilityId, role, rosterCount, names, configHref, monthHref, onAddWorker, engineLog, bridge, isDemo = false }: TodayPanelProps) {
  // While a demo seat is live the reads poll every 5 seconds (open shift lifecycle plan section 11.3): a read moves the board, so the stages are exact to the poll.
  const [demoLive, setDemoLive] = useState(false)
  const decisions = useDecisions(facilityId, demoLive ? DEMO_REFRESH_MS : undefined)
  const today = todayIn(decisions.data?.timezone)
  const period = today.slice(0, 7)
  const board = useScheduleBoard(facilityId, period, demoLive ? DEMO_REFRESH_MS : undefined)
  const liveNow = (board.data?.postings ?? []).some((posting) => posting.bornOf === 'demo' && posting.status === 'open')
  useEffect(() => {
    setDemoLive(liveNow)
  }, [liveNow])
  const health = useMonthHealth(facilityId)
  const heatmap = useFacilityHeatmap(facilityId, period)
  const week = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(today, index)), [today])
  const nextPeriod = addDays(today, 6).slice(0, 7)
  // The week may run into next month, whose board is read only then (a month not released answers no board).
  const nextBoard = useScheduleBoard(nextPeriod !== period ? facilityId : null, nextPeriod)
  // The week may run into next month, whose heatmap is read only then.
  const nextHeatmap = useFacilityHeatmap(nextPeriod !== period ? facilityId : null, nextPeriod)
  const weekRequirements = useCensusRequirementsRange(facilityId, { from: today, to: addDays(today, 6) })
  const requirementsOn = (date: string) => weekRequirements.data?.days.find((day) => day.date === date) ?? null
  // Today is the range's first day: one read serves the Census tile and the week strip.
  const todayRequirements = requirementsOn(today)
  const [chosenDay, setChosenDay] = useState<string | null>(null)
  const [engineOpen, setEngineOpen] = useState(false)
  const shownDay = chosenDay !== null && week.includes(chosenDay) ? chosenDay : today
  const shownPeriod = shownDay.slice(0, 7)
  const [filter, setFilter] = useTodayFilter(facilityId)
  const steps = setupSteps(health.data ?? null, rosterCount)
  const units = useMemo(() => (todayRequirements?.units ?? []).map((row) => ({ id: row.unit.id, name: row.unit.name })), [todayRequirements])
  const boardView = board.data ?? null
  const configQuery = useFacilityConfig(facilityId)
  const config = configQuery.data
  // Read again with every refresh of the decisions, so a shift that ends while the page is open turns grey on the next read.
  const clock: CoverageClock = { today, time: timeIn(decisions.data?.timezone), spans: Object.fromEntries(schedulingRulesOf(config ?? null).shiftTypes.map((type) => [type.key, { start: type.start, end: type.end }])) }
  const coverage = coverageToday(todayRequirements, boardView, today, filter, clock)
  const census = coverage.reduce((sum, row) => sum + (row.census ?? 0), 0)
  // The day's verdict for the tile, from the same reading as the heatmap below it.
  const todayHeat = heatmap.data?.period === period ? heatmap.data : null
  const todayDay = todayHeat?.days.find((day) => day.date === today) ?? null
  const glance = todayHeat && todayDay ? filteredDay(todayDay, filter, todayHeat.scale) : null
  const beds = (todayRequirements?.units ?? []).filter((row) => filter.unitId === null || row.unit.id === filter.unitId).reduce((sum, row) => sum + (row.unit.beds ?? 0), 0)
  const open = openSeatsInFlight(boardView, filter)
  const counts = decisions.data?.counts
  const periodQuery = useSchedulePeriod(facilityId, period)
  const periodView = periodQuery.data ?? null
  // Design screen 10: Today waits on several reads, so it says what is on its way.
  const notReleased = (error: unknown) => ((error instanceof ApiError && error.status === 409) || isNotFoundError(error) ? 'Not released yet.' : null)
  useLoadingWindow({
    shown,
    visit: `today:${facilityId}`,
    title: 'Opening Today',
    rank: 1,
    sources: [
      { key: 'today-decisions', label: 'What needs you', query: decisions },
      { key: 'today-health', label: 'The month’s health', query: health },
      { key: 'today-week', label: 'The week’s census and what it requires', query: weekRequirements },
      { key: 'today-board', label: 'The board, brought up to now', query: board, slowNote: 'Still bringing the board up to now. A busy month takes longer.', answered: notReleased },
      { key: 'today-next-board', label: 'Next month’s board (the week runs into it)', query: nextBoard, answered: notReleased },
      { key: 'today-period', label: 'Where the month stands', query: periodQuery },
      { key: 'today-heatmap', label: 'The day’s state, slot by slot', query: heatmap },
      { key: 'today-next-heatmap', label: 'Next month’s days (the week runs into it)', query: nextHeatmap },
      { key: 'today-settings', label: 'The facility’s settings', query: configQuery },
    ],
  })
  const releaseMode = config?.scheduling?.release?.mode ?? 'after_review'
  const censusMode = config?.scheduling?.census?.mode ?? 'automatic'
  const engineLabel = engineLabelOf(releaseMode, censusMode)
  const engineAlone = releaseMode !== 'staff' && censusMode !== 'propose'
  const monthLine = periodView ? `${monthName(period)} ${periodView.status === 'released' ? 'is released' : periodView.status === 'built' ? 'is built, not released' : periodView.status === 'closed' ? 'is closed to bidding' : 'is taking bids'}` : null
  const checkedAt = decisions.data ? new Date(decisions.data.evaluatedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : null
  const refreshing = decisions.isFetching || board.isFetching || weekRequirements.isFetching || heatmap.isFetching
  const refresh = () => Promise.all([decisions.refetch(), board.refetch(), weekRequirements.refetch(), health.refetch(), heatmap.refetch()])
  const heatUnitName = (unitId: string) => heatmap.data?.units.find((unit) => unit.id === unitId)?.name ?? unitId
  const boardOn = (date: string) => (date.slice(0, 7) === period ? boardView : (nextBoard.data ?? null))
  const weekDays = week.map((date) => weekDaySummary(requirementsOn(date), boardOn(date), date, filter, clock))
  const shownHeatmap = shownPeriod === period ? heatmap : nextHeatmap
  const shownHeat = shownHeatmap.data?.period === shownPeriod ? shownHeatmap.data : null
  const goToCensus = () => document.getElementById(CENSUS_ANCHOR)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  // Beside the heatmap's grid (design screen 13): the seats in the engine's hands, next to the cells that say what is short.
  const openShiftsAside = (
    <>
      <div className="flex flex-wrap items-baseline gap-2">
        <h3 className="text-[11px] font-bold uppercase tracking-wide text-marine">Open shifts in flight</h3>
        <span className="text-[11px] text-ink-mid">offered to eligible workers only, in the facility's order</span>
      </div>
      <OpenShiftsInFlight board={boardView} filter={filter} names={names} units={unitNamesOf(units)} canOpenCallout={role === 'facility_administrator'} demo={isDemo ? { facilityId, period } : null} />
    </>
  )

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="text-2xl font-black text-ink">Today</h1>
        <span className="text-xs text-ink-mid">{[longDay(today), monthLine, checkedAt ? `last engine check ${checkedAt}` : null].filter(Boolean).join(' · ')}</span>
        <button type="button" title="What does this mean?" aria-haspopup="dialog" onClick={() => setEngineOpen(true)} className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold underline-offset-2 hover:underline ${engineAlone ? 'bg-green-lt text-green' : 'bg-marine-lt text-marine-dk'}`}>
          {engineLabel}
        </button>
        {engineOpen && <EngineModeModal releaseMode={releaseMode} reviewDays={config?.scheduling?.release?.reviewDays ?? 4} censusMode={censusMode} configHref={configHref} onClose={() => setEngineOpen(false)} />}
        <span className="flex-1" />
        <RefreshButton size="sm" onRefresh={refresh} refreshing={refreshing} />
      </div>
      <FilterBar filter={filter} onChange={setFilter} units={units} />

      <LeadSection
        title={steps ? 'Set up this facility' : 'Needs you'}
        count={steps ? `${steps.filter((step) => step.done).length} of ${steps.length} done` : counts ? String(counts.total) : null}
        alert={!steps && (counts?.total ?? 0) > 0}
        hint={steps ? 'each step unlocks the next; this list goes away when it is done' : counts ? `${counts.urgent} urgent · ${counts.approval} approvals · ${counts.proposed} proposed · ${counts.reminder} reminders · only what the engine may not decide alone, most urgent first` : 'only what the engine may not decide alone'}
        help="schedule.today"
      >
        {steps ? (
          <SetupList steps={steps} configHref={configHref} monthHref={monthHref} onAddWorker={onAddWorker} onRecordCensus={goToCensus} />
        ) : decisions.isError ? (
          <LoadFailure size="xs" message="Could not load what needs you." onRetry={() => void decisions.refetch()} retrying={decisions.isFetching} />
        ) : decisions.data ? (
          <NeedsYou facilityId={facilityId} view={decisions.data} filter={filter} canOpenCallout={role === 'facility_administrator'} onRecordCensus={goToCensus} monthHref={monthHref} released={boardView !== null} />
        ) : (
          <Skeleton lines={4} label="Loading what needs you" />
        )}
      </LeadSection>

      <div className="flex flex-wrap gap-3">
        <Tile label="Census today" value={coverage.length > 0 ? census : '—'} note={coverage.length > 0 ? `${beds > 0 ? `of ${beds} beds · ` : ''}${coverage.filter((row) => row.census !== null).length} of ${coverage.length} units counted or forecast` : 'No units yet, so nothing to count'} />
        <Tile
          label="The day"
          value={!todayHeat || !todayHeat.hasStaffingPlan || !glance || glance.tally.required === 0 ? '—' : !todayHeat.released ? 'Not released' : HEAT_SWATCH[glance.state].label}
          tone={todayHeat?.released && glance && (glance.state === 'short' || glance.state === 'critical') ? 'alert' : 'plain'}
          note={
            !todayHeat
              ? 'Reading the day’s state'
              : !todayHeat.hasStaffingPlan
                ? 'No staffing plan to judge the day by'
                : !todayHeat.released
                  ? `${monthName(period)} has no released schedule, so nothing is held yet`
                  : !glance || glance.tally.required === 0
                    ? 'Nothing is required today'
                    : `${glance.tally.staffed} of ${glance.tally.required} seats held · ${pctOf(glance.tally.staffed, glance.tally.required)}${glance.tally.onBoard > 0 ? ` · ${glance.tally.onBoard} on the board` : ''}${glance.tally.uncovered > 0 ? ` · ${glance.tally.uncovered} not on the board, under Needs you` : ''}`
          }
        />
        <Tile label="Open shifts" value={boardView ? open.length : '—'} note={boardView ? `${open.filter((posting) => posting.phase === 'offers').length} in offers · ${open.filter((posting) => posting.phase === 'first_come').length} first come · ${open.filter((posting) => posting.phase === 'locked').length} too close to offer` : 'The open board starts with the release'} />
      </div>

      <Section
        title={shownDay === today ? 'The day at a glance' : `The day at a glance · ${longDay(shownDay)}`}
        hint={shownDay === today ? "every unit, role and shift against what today's census requires · solid once worked or under way, striped while ahead" : 'every unit, role and shift against that day’s census · striped: still ahead'}
        help="schedule.heatmap"
      >
        {shownDay !== today && (
          <div className="flex flex-wrap items-center gap-3 text-[11px]">
            <button type="button" className="font-bold text-marine underline hover:text-marine-dk" onClick={() => setChosenDay(null)}>
              Back to today
            </button>
            <a className="font-bold text-marine underline hover:text-marine-dk" href={`${monthHref}&detail=census`}>
              Census planning and what-if
            </a>
          </div>
        )}
        {shownHeatmap.isError ? (
          <LoadFailure size="xs" message="Could not load the day’s state." onRetry={() => void shownHeatmap.refetch()} retrying={shownHeatmap.isFetching} />
        ) : (
          <DayHeatmap view={shownHeat} date={shownDay} now={clock.time} filter={filter} unitName={heatUnitName} aside={openShiftsAside} />
        )}
      </Section>

      <div className="grid items-start gap-5 lg:grid-cols-[2fr_3fr]">
        <Section title="Census" hint="recorded once a shift; the engine answers">
          <TodayCensus facilityId={facilityId} today={today} filter={filter} anchorId={CENSUS_ANCHOR} />
          <WeekAhead days={weekDays} selected={shownDay} onSelect={(date) => setChosenDay(date === today ? null : date)} released={boardView !== null} />
        </Section>
        <Section title="What the engine did" hint="every automatic change, newest first">
          {engineLog}
        </Section>
      </div>

      {/* The callouts card carries its own heading and Open a callout. */}
      <section className="min-w-0 rounded border border-marine/15 bg-white px-4 py-3.5">{bridge}</section>
    </div>
  )
}
